import yaml from 'js-yaml';
import type { FlowDefinition } from './types';

/**
 * Canvas edit model → Flow IR serialization — the inverse of parse.ts (spec §2, tenet 4: the YAML
 * pane and the canvas are two projections of one document, lossless by construction).
 *
 * LOSSLESSNESS / CARRY-THROUGH: the designer store holds the parsed FlowDefinition object with its
 * ORIGINAL keys intact at runtime — the RATIFIED node schema permits additional properties, so any
 * field the TS read-model does not name (a forward-compatible or engine-internal key) still lives on
 * the object and survives here. Edits spread the original node/edge objects (mutations.ts), never
 * rebuild them from a known-field whitelist, so unknown fields are NEVER dropped. Layout/position is
 * deliberately NOT serialized: the IR schema carries no position field (layout is deterministic and
 * ignored by the diff engine — spec §4.6), so drag positions live in canvas state only.
 */

/** A defensive deep clone that also drops `undefined`-valued keys (pure-JSON IR). */
export function cloneIr(def: FlowDefinition): FlowDefinition {
  return JSON.parse(JSON.stringify(def)) as FlowDefinition;
}

/** Serialize to the canonical JSON wire form (the server canonicalises per RFC 8785 before signing). */
export function toJsonSource(def: FlowDefinition): string {
  return JSON.stringify(cloneIr(def), null, 2);
}

/** Serialize to the YAML authoring form. */
export function toYamlSource(def: FlowDefinition): string {
  return yaml.dump(cloneIr(def), { noRefs: true, lineWidth: 120, sortKeys: false });
}
